import React, { useState } from "react";

interface NocActionModalProps {
  hRemarks: string;
  onHRemarksChange: (remarks: string) => void;
  onApproveReject: (action: "A" | "R") => Promise<void> | void;
  isProcessing: boolean;
  processingAction: "A" | "R" | null;
  showRemarksSection?: boolean;
  remarksRequiredForReject?: boolean;
}

export const NocActionModal: React.FC<NocActionModalProps> = ({
  hRemarks,
  onHRemarksChange,
  onApproveReject,
  isProcessing,
  processingAction,
  showRemarksSection = true,
  remarksRequiredForReject = true,
}) => {
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    action: "A" | "R" | null;
  }>({ show: false, action: null });
  const [remarksError, setRemarksError] = useState<boolean>(false);

  const handleTriggerAction = (action: "A" | "R") => {
    if (action === "R" && remarksRequiredForReject && !(hRemarks || "").trim()) {
      setRemarksError(true);
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }
    setRemarksError(false);
    setConfirmModal({ show: true, action });
  };

  const handleConfirmAction = () => {
    const action = confirmModal.action;
    setConfirmModal({ show: false, action: null });
    if (action) {
      onApproveReject(action);
    }
  };

  return (
    <>
      {/* Remarks Section */}
      {showRemarksSection && (
        <div className="card mb-4 border-primary-subtle shadow-sm">
          <div className="card-header bg-light d-flex align-items-center">
            <h5 className="card-title mb-0 text-primary">
              <i className="ti ti-message-dots me-2"></i>
              Approver Remarks & Action
            </h5>
          </div>
          <div className="card-body">
            <div className="mb-0">
              <label className="form-label fw-bold d-flex align-items-center justify-content-between">
                <span>
                  Your Remarks {remarksRequiredForReject && <span className="text-danger">*</span>}
                  <span className="text-muted fw-normal small ms-2">
                    (Mandatory for rejection)
                  </span>
                </span>
              </label>
              <textarea
                id="approver-remarks-textarea"
                className={`form-control ${remarksError ? "is-invalid border-danger" : ""}`}
                rows={3}
                placeholder="Write Something... (Mandatory if rejecting)"
                value={hRemarks}
                onChange={(e) => {
                  onHRemarksChange(e.target.value);
                  if (e.target.value.trim()) setRemarksError(false);
                }}
              />
              {remarksError && (
                <div className="invalid-feedback d-block mt-1">
                  Please enter approver remarks before rejecting the application.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="card mb-4 border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-success px-4 py-2 d-flex align-items-center"
              onClick={() => handleTriggerAction("A")}
              disabled={isProcessing}
            >
              {isProcessing && processingAction === "A" ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Processing...
                </>
              ) : (
                <>
                  <i className="ti ti-check me-2 fs-16"></i>
                  Approve
                </>
              )}
            </button>
            <button
              className="btn btn-danger px-4 py-2 d-flex align-items-center"
              onClick={() => handleTriggerAction("R")}
              disabled={isProcessing}
            >
              {isProcessing && processingAction === "R" ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Processing...
                </>
              ) : (
                <>
                  <i className="ti ti-x me-2 fs-16"></i>
                  Reject
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1080 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {confirmModal.action === "A" ? "Approve Application" : "Reject Application"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setConfirmModal({ show: false, action: null })}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Are you sure you want to{" "}
                  <strong>{confirmModal.action === "A" ? "approve" : "reject"}</strong> this NOC application?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setConfirmModal({ show: false, action: null })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${confirmModal.action === "A" ? "btn-success" : "btn-danger"}`}
                  onClick={handleConfirmAction}
                >
                  Yes, {confirmModal.action === "A" ? "Approve" : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
